import React, { FC, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useInterval } from 'react-use';

import { getOperationsTranslated, getSkill } from 'client/api';
import { getAgreements } from 'client/api/services/userAgreement';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { routes } from 'client/lib/routes';
import { Operation } from 'client/model/operation';
import { Skill } from 'client/model/skill';
import { UserAgreement } from 'client/model/userAgreement';
import { displayUserAgreements, isAliceSkillOrSmartHomeSkill, isIframe, makeSafe } from 'client/utils';
import { tycoon } from 'client/utils/messages';
import { userAgreementsComparator } from 'client/utils/validators';

import { AliceSkillAppPage } from './AliceSkillAppPage';

interface Props {
    skillId: string;
}

export const SkillAppPageContainer: FC<Props> = ({ skillId }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [skill, setSkill] = useState<Skill>();
    const [operations, setOperations] = useState<Operation[]>([]);
    const [userAgreements, setUserAgreements] = useState<UserAgreement[]>([]);
    const [userAgreementsDraft, setUserAgreementsDraft] = useState<UserAgreement[]>([]);

    const fetchSkill = useCallback(async () => {
        setIsLoading(true);
        let fetchedSkill;
        try {
            fetchedSkill = await getSkill(skillId);
        } catch (err) {
            console.error('@Error in fetch getSkill', err);
        }

        // TODO: remove when move to getSkill from app-store-backend
        if (fetchedSkill?.id && displayUserAgreements()) {
            try {
                const uaDraftPromise = getAgreements(fetchedSkill.id, true, fetchedSkill.channel).then((result) =>
                    result.sort(userAgreementsComparator),
                );
                const uaPromise = getAgreements(fetchedSkill.id, false, fetchedSkill.channel).then((result) =>
                    result.sort(userAgreementsComparator),
                );
                const [fetchedUaDraft, fetchedUa] = await Promise.all([uaDraftPromise, uaPromise]);
                setUserAgreements(fetchedUa);
                setUserAgreementsDraft(fetchedUaDraft);
            } catch (err) {
                console.error('@Error to fetch userAgreements', err);
            }
        }
        if (!fetchedSkill && isIframe()) {
            tycoon.sendChatNotFound(skillId);
        }
        setSkill(fetchedSkill);
        setIsLoading(false);
    }, [skillId]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchOperations = useCallback(
        makeSafe(async () => {
            setOperations(await getOperationsTranslated(skillId));
        }),
        [skillId],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void fetchOperations(), [skillId]);

    useInterval(fetchOperations, 1 * 60 * 1000);

    useEffect(() => {
        fetchSkill();
    }, [skillId, fetchSkill]);

    if (isLoading && !skill) {
        return <PageLoader />;
    }

    if (!skill) {
        return <Navigate to={routes.app.skills} replace />;
    }

    return (
        <CurrentAliceAppContext.Provider
            value={{
                aliceApp: skill,
                operations,
                addOperation: () => {},
                fetchOperations,
                refreshAliceApp: fetchSkill,
            }}
        >
            <Routes>
                <Route
                    index
                    element={
                        isAliceSkillOrSmartHomeSkill(skill) ? (
                            <AliceSkillAppPage
                                skill={skill}
                                isDraft
                                userAgreements={userAgreements}
                                userAgreementsDraft={userAgreementsDraft}
                            />
                        ) : null
                    }
                />
                <Route
                    path="draft/*"
                    element={
                        isAliceSkillOrSmartHomeSkill(skill) ? (
                            <AliceSkillAppPage
                                skill={skill}
                                isDraft
                                userAgreements={userAgreements}
                                userAgreementsDraft={userAgreementsDraft}
                            />
                        ) : null
                    }
                />
                <Route
                    path="published/*"
                    element={
                        isAliceSkillOrSmartHomeSkill(skill) ? (
                            <AliceSkillAppPage
                                skill={skill}
                                isDraft={false}
                                userAgreements={userAgreements}
                                userAgreementsDraft={userAgreementsDraft}
                            />
                        ) : null
                    }
                />
                <Route
                    path="*"
                    element={
                        isAliceSkillOrSmartHomeSkill(skill) ? (
                            <AliceSkillAppPage
                                skill={skill}
                                isDraft={false}
                                userAgreements={userAgreements}
                                userAgreementsDraft={userAgreementsDraft}
                            />
                        ) : null
                    }
                />
            </Routes>
        </CurrentAliceAppContext.Provider>
    );
};
